import { DateTime } from "luxon";
import { YearlessDate } from "../../../../components/DateFieldYearless/types";

/**
 * Yearless date format preset options
 */
export type YearlessDateFormatPreset = "short" | "long";

/**
 * Options type for the yearless date formatter function
 */
export type YearlessDateFormatterOptions = {
  /**
   * The locale to use for the yearless date formatter
   * @default "en-US"
   */
  locale?: Intl.LocalesArgument;
  /**
   * The format preset or Intl.DateTimeFormatOptions object
   * - "short": Jan 05
   * - "long": January 05
   * - Intl.DateTimeFormatOptions: Custom format (e.g., { month: "long", day: "numeric" })
   * @default "short"
   */
  format?: YearlessDateFormatPreset | Intl.DateTimeFormatOptions;
};

const formatPresets: Record<
  YearlessDateFormatPreset,
  Intl.DateTimeFormatOptions
> = {
  short: { month: "short", day: "2-digit" },
  long: { month: "long", day: "2-digit" },
};

/**
 * Helper function for formatting table cells with month and day (no year), including i18n support
 * @param value - The yearless date value to format. Accepts:
 *   - Temporal PlainMonthDay string format: "--MM-DD" (e.g., "--01-15")
 *   - YearlessDate object: { month: number, day: number }
 * @param options - The options for the yearless date formatter
 * @returns The formatted month-day string or null for undefined/null values
 */
export const yearlessDateFormatter = (
  value?: string | YearlessDate | null,
  { locale = "en-US", format = "short" }: YearlessDateFormatterOptions = {},
) => {
  if (value == null) {
    return null;
  }

  let month: number;
  let day: number;

  if (typeof value === "string") {
    // Parse the --MM-DD format (Temporal PlainMonthDay string format)
    const match = value.match(/^--(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }
    month = parseInt(match[1], 10);
    day = parseInt(match[2], 10);
  } else {
    // YearlessDate object format
    if (value.month == null || value.day == null) {
      return null;
    }
    month = value.month;
    day = value.day;
  }

  // Convert locale to string for Luxon compatibility
  const localeString = locale?.toString() ?? "en-US";

  // Create a DateTime with a leap year reference to support Feb 29
  const dateTime = DateTime.fromObject(
    {
      year: 2000,
      month,
      day,
    },
    { locale: localeString },
  );

  if (!dateTime.isValid) {
    return null;
  }

  // Check if format is a preset string or Intl options object
  if (typeof format === "string") {
    const preset = formatPresets[format];
    return dateTime.toLocaleString(preset);
  }

  // Custom Intl.DateTimeFormatOptions
  return dateTime.toLocaleString(format);
};
